<template>
  <div class="cost-management">
    <b-card v-if="showForm || showFormPreview">
      <preview-form
        showImportCSVFile
        :importCSVFileName="CSVFileName"
        :formName="formData.name"
        :moduleSlug="moduleSlug"
        :label="$t(`${moduleSlug}.name-label`)"
        isMultisite
        :siteLabel="$t(`${moduleSlug}.selected_site-label`)"
        :showProductGroups="false"
        :importedFromCSVLabel="$t(`${moduleSlug}.import_csv-label`)"
        showStartDate
        :startDateLabel="$t(`${moduleSlug}.start_date-label`)"
        showEndDate
        :endDateLabel="$t(`${moduleSlug}.end_date-label`)"
        :startDate="formattedStartDate"
        :endDate="formattedEndDate"
        v-if="showFormPreview"
      />
      <b-form @submit.prevent v-if="showForm">
        <div class="row">
          <div class="col-sm-12 col-md-6">
            <b-form-group
              id="name"
              :label="$t(`${moduleSlug}.name-label`)"
              label-for="name"
              :label-class="['form-label', 'required']"
              :class="{ 'form-group--error': $v.formData.name.$error }"
            >
              <b-form-input
                id="name"
                v-model="formData.name"
                :placeholder="$t(`${moduleSlug}.name-placeholder`)"
                trim
                :class="{ 'is-invalid': $v.formData.name.$error }"
              ></b-form-input>
              <b-form-invalid-feedback id="name-feedback">
                {{ $t(`${moduleSlug}.name-required-error-msg`) }}
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
          <div class="col-sm-12 col-md-6">
            <b-form-group
              id="site"
              :label="$t(`${moduleSlug}.select_site-label`)"
              label-for="site"
              :label-class="['form-label', 'required']"
              :class="{ 'form-group--error': $v.formData.sites.$error }"
            >
              <b-dropdown
                id="cost-site-dropdown"
                :text="$t(`${moduleSlug}.select_site-placeholder`)"
                ref="dropdown"
                block
                menu-class="w-100 scrollable"
                no-caret
                lazy
                variant="brand-dropdown"
                :toggle-class="{ 'is-invalid': $v.formData.sites.$error }"
                :class="{ 'is-invalid': $v.formData.sites.$error }"
              >
                <b-dropdown-form @submit.stop.prevent>
                   <b-form-checkbox :checked="selectAll" @change="toggleSelectAll" class="mb-2">
                    {{ selectAll ?   $t(`general.unselect-all`)  : $t(`general.select-all`) }}
                   </b-form-checkbox>
                  <hr class="mt-0 mb-2" />
                  <b-form-checkbox-group
                    class="scrollable"
                    id="sites"
                    v-model="formData.sites"
                    :options="siteOptions"
                    name="sites"
                  ></b-form-checkbox-group>
                </b-dropdown-form>
              </b-dropdown>
              <b-form-invalid-feedback id="sites-feedback">
                {{ $t(`${moduleSlug}.select_site-required-error-msg`) }}
              </b-form-invalid-feedback>
              <div class="row mt-3" v-if="formData.sites.length">
                <div class="col-12">
                  <div class="tags-group">
                    <div
                      class="tag tags_title"
                      v-for="(_selected, index) in formData.sites"
                      :key="'site_' + index"
                    >
                      <div class="d-flex align-items-center">
                        <span class="align-middle mr-1">
                          {{ _selected.siteId }} - {{ _selected.siteName }}
                        </span>
                        <i
                          class="icon-close"
                          @click="deleteSite(index)"
                          role="button"
                        ></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-12 col-md-6">
            <div id="startDate" role="group" class="form-group">
              <label for="startDate" class="form-label required">
                {{ $t(`${moduleSlug}.start_date-label`) }}
              </label>
              <div
                class="flex w-full"
                :class="{ 'is-invalid': $v.formData.priceStartDate.$error }"
              >
                <v-date-picker
                  :locale="selected_language"
                  ref="priceStartDate"
                  class="flex-grow"
                  :class="{ 'is-invalid': $v.formData.priceStartDate.$error }"
                  :masks="masks"
                  v-model="formData.priceStartDate"
                  is-required
                  :popover="{ visibility: 'click' }"
                  :model-config="modelConfig"
                  :disabled-dates="[
                    {
                      start: null,
                      end: yesterday,
                    },
                  ]"
                  :available-dates="{
                    start: new Date(formData.priceStartDate),
                    end: new Date(formData.priceStartDate),
                  }"
                >
                  <template v-slot="{ inputValue }">
                    <div class="d-flex custom-datepicker">
                      <input
                        class="form-control bg-white rounded-top-right-0 rounded-bottom-right-0"
                        :class="{
                          'is-invalid': $v.formData.priceStartDate.$error,
                        }"
                        :value="inputValue"
                        @click="showStartDatePicker"
                        :placeholder="dateFormat"
                        readonly
                      />
                      <div
                        class="picker-icon"
                        :class="{
                          'is-invalid': $v.formData.priceStartDate.$error,
                        }"
                        role="button"
                        @click="showStartDatePicker"
                      >
                        <i class="icon-date"></i>
                      </div>
                    </div>
                  </template>
                </v-date-picker>
              </div>

              <b-form-invalid-feedback id="startDate-feedback">
                {{
                  formData.priceStartDate &&
                  $v.formData.priceStartDate.$error &&
                  $v.formData.priceStartDate.minValue == false
                    ? sytemMessages.minimumDateError
                    : sytemMessages.startDateRequired
                }}
              </b-form-invalid-feedback>
            </div>
          </div>
          <div class="col-sm-12 col-md-6">
            <div id="endDate" role="group" class="form-group">
              <label for="endDate" class="form-label">
                {{  $t(`${moduleSlug}.end_date-label`) }}
              </label>
              <i class="icon-tooltip ml-1" id="endDate-tootltip"></i>
              <b-tooltip target="endDate-tootltip"
                ><p v-html="$t(`${moduleSlug}.end_date-tooltip`)"
              /></b-tooltip>
              <div
                class="flex w-full"
                :class="{ 'is-invalid': $v.formData.priceEndDate.$error }"
              >
                <v-date-picker
                  :locale="selected_language"
                  ref="priceEndDate"
                  class="flex-grow"
                  :masks="masks"
                  v-model="formData.priceEndDate"
                  :popover="{ visibility: 'click' }"
                  :model-config="modelConfig"
                  :disabled-dates="[
                    {
                      start: null,
                      end: yesterday,
                    },
                  ]"
                  :available-dates="{
                    start:
                      formData.priceEndDate != ''
                        ? new Date(formData.priceEndDate)
                        : new Date(),
                    end:
                      formData.priceEndDate != ''
                        ? new Date(formData.priceEndDate)
                        : null,
                  }"
                >
                  <template v-slot="{ inputValue }">
                    <div class="d-flex custom-datepicker">
                      <input
                        class="form-control bg-white rounded-top-right-0 rounded-bottom-right-0"
                        :class="{
                          'is-invalid': $v.formData.priceEndDate.$error,
                        }"
                        :value="inputValue"
                        @click="showEndDatePicker"
                        :placeholder="dateFormat"
                        readonly
                      />
                      <div
                        class="picker-icon"
                        :class="{
                          'is-invalid': $v.formData.priceEndDate.$error,
                        }"
                        role="button"
                        @click="showEndDatePicker"
                      >
                        <i class="icon-date"></i>
                      </div>
                    </div>
                  </template>
                </v-date-picker>
              </div>
              <b-form-invalid-feedback id="priceEndDate-feedback">
                {{ $t(`${moduleSlug}.end_date-invalid-error-msg`) }}
              </b-form-invalid-feedback>
            </div>
          </div>
        </div>

        <div
          id="csvFileDiv"
          class="form-group"
          role="group"
          :class="{
            'form-group--error':
              $v.formData.csvFile.$error ||
              invalidCSV ||
              emptyCSV ||
              emptySkuCsv ||
              emptyPriceCsv ||
              duplicateCSVData,
          }"
        >
          <label class="form-label required">
            {{ $t(`${moduleSlug}.import_csv-label`) }}
          </label>
          <div
            class="d-flex align-items-start flex-column"
            :class="{
              'is-invalid':
                $v.formData.csvFile.$error ||
                invalidCSV ||
                emptyCSV ||
                emptySkuCsv ||
                emptyPriceCsv ||
                duplicateCSVData,
            }"
          >
            <div class="template-div mb-3">
              <b-link
                @click="downloadCSVTemplate"
                class="btn btn-brand-secondary"
                >{{ $t(`${moduleSlug}.download_csv_template-label`)  || 'Download template' }}</b-link
              >
            </div>
            <div
              class="upload-btn-wrapper d-flex align-items-md-center flex-column flex-md-row align-items-sm-start"
            >
              <div>
                <b-form-file
                  ref="csvFile"
                  id="csvFile"
                  v-model="formData.csvFile"
                  plain
                  hidden
                  accept=".csv"
                />
                <label
                  for="csvFile"
                  class="btn btn-brand-secondary mb-2 mb-md-0"
                  >{{ $t(`${moduleSlug}.csv_browse-label`) }}</label
                >
              </div>
              <span id="file-chosen"
                >{{
                  formData.csvFile ? formData.csvFile.name : $t(`${moduleSlug}.no_csv-placeholder`)
                }}
                <b-link v-if="formData.csvFile" @click="removeCSVFile">
                  <i class="icon-close"></i>
                </b-link>
              </span>
            </div>
          </div>

          <b-form-invalid-feedback id="csvFile-feedback" v-if="emptyCSV">
           {{ $t(`${moduleSlug}.empty_csv-error-msg`) }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback id="csvFile-feedback" v-else-if="invalidCSV">
            {{ $t(`${moduleSlug}.invalid_csv-error-msg`) }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback id="csvFile-feedback" v-else-if="emptySkuCsv">
            {{ $t(`${moduleSlug}.invalid_csv_data-error-msg`) }}
            
          </b-form-invalid-feedback>
          <b-form-invalid-feedback id="csvFile-feedback" v-else-if="emptyPriceCsv">
            {{ $t(`${moduleSlug}.missing-cost-error-msg`) }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback id="csvFile-feedback" v-else-if="duplicateCSVData">
            {{ $t(`${moduleSlug}.duplicate_csv_data-error-msg`) }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback id="csvFile-feedback" v-else>
            {{
              $v.formData.csvFile.fileValidation == false
                ? $t(`${moduleSlug}.invalid_csv_file-error-msg`)
                : $t(`${moduleSlug}.import_csv-error-msg`)
            }}
          </b-form-invalid-feedback>
        </div>
      </b-form>

      <div class="d-flex flex-row-reverse">
        <b-button
          id="validate-btn"
          @click="handleValidate"
          v-if="showForm && !showValidationTable"
          variant="brand-primary"
        >
        {{ $t(`${moduleSlug}.validate-btn-label`) }}
        </b-button>
        <b-button
          id="submit-btn"
          @click="confirmSubmit"
          v-if="showValidationTable && !hasErrors && (mode !== 'edit' || csvValidated)"
          variant="brand-primary"
          
        >
        {{ $t(`${moduleSlug}.submit-btn-label`) }}
        </b-button>
        <b-button
          id="edit-btn"
          @click="handleEdit"
          v-if="showFormPreview || showValidationTable"
          variant="brand-secondary"
          class="mr-3"
        >
        {{ $t(`${moduleSlug}.edit-btn-label`) }}
        </b-button>
      </div>
    </b-card>

    <b-card v-if="showValidationTable || showFormPreview" class="mt-4">
      <div
        class="d-flex flex-row-reverse mb-3"
        v-if="mode === 'edit' && (showFormPreview || showValidationTable)"
      >
        <b-link
          class="export-csv my-md-0 my-2"
          @click="downloadProductPreview"
          :disabled="!validationRows.length"
        >
        {{ $t(`general.export-csv`) }}
          <i class="icon-download ml-1"></i>
        </b-link>
      </div>
      <!-- <b-table
        hover
        responsive
        :items="validationRows"
        :fields="validationFields"
        tbody-tr-class="text-nowrap"
        show-empty
        empty-text="No data"
      > -->
      <b-table
        hover
        responsive
        :items="validationRows"
        :fields="validationFields"
        :tbody-tr-class="rowClass"
        show-empty
        empty-text="No data"
      >
        <template #cell(product)="data">
          <div class="filtered-products-content">
            <template v-if="detectSkuNotMatch(data.item)">
              <span class="product-dash-icon-slot">-</span>
              <span class="product-dash-name-slot">-</span>
            </template>
            <template v-else>
              <b-img
                src="/media/icons/rectangle.svg"
                alt="Product Rectangle Icon"
                class="product-icon"
              />
              <span class="product-name-text">{{ data.item.productName }}</span>
            </template>
          </div>
        </template>
        <template #cell(stores)="data">
          <div class="d-flex flex-wrap align-items-center stores-cell-badges">
            <span
              v-for="(store, i) in ((data.item.stores || []).length > 7 ? data.item.stores.slice(0, 7) : (data.item.stores || []))"
              :key="i"
              class="badge-orange-badge"
            >
              {{ store }}
            </span>
            <span
              v-if="(data.item.stores || []).length > 7"
              class="badge-orange-badge"
              v-b-tooltip.hover="data.item.stores.slice(7).join(', ')"
            >
              +{{ data.item.stores.length - 7 }}
            </span>
          </div>
        </template>

        <template #cell(vmn)="data">
          <span v-if="data.item.skuNotMatch" class="import-cell-placeholder">-</span>
          <span v-else>{{ data.item.vmn }}</span>
        </template>

        <template #cell(price)="data">
          <span
            v-if="data.item.skuNotInSite || data.item.skuNotMatch"
            class="import-cell-placeholder"
            >-</span
          >
          <span v-else class="price-text read-only">$&nbsp;{{ data.item.price }}</span>
        </template>
        <!-- <template #cell(id)="data">
          {{ data.item.productId }}
        </template> -->
        <template #cell(sku)="data">
          {{ data.item.productSKU }}
        </template>
        <template #cell(status)="data">
          <div class="status-icon-container">

            <i
              v-if="data.item.status === 'valid'"
              class="icon-success"
              v-b-tooltip.hover
              :title="data.item.statusMessage"
            ></i>

            <i
              v-else-if="data.item.status === 'error'"
              class="icon-error text-danger"
              v-b-tooltip.hover
              :title="data.item.statusMessage"
            ></i>

            <i
              v-else
              class="icon-warning text-warning"
              v-b-tooltip.hover
              :title="data.item.statusMessage"
            ></i>

          </div>
        </template>
        <template #cell(cost)="data">
          <span v-if="data.item.skuNotInSite || data.item.skuNotMatch" class="import-cell-placeholder">-</span>
          <div v-else class="price-edit-container">
            <div
              v-if="
                editingIndex !== data.index ||
                !canEditValidationRows ||
                data.item.status !== 'valid'
              "
              @click="
                canEditValidationRows &&
                  data.item.status === 'valid' &&
                  (editingIndex = data.index)
              "
              :class="{
                'price-text': true,
                clickable: canEditValidationRows && data.item.status === 'valid',
              }"
            >
            $&nbsp;{{ data.item.cost }}
            </div>
            <b-form-input
              v-else
              v-model="data.item.cost"
              type="number"
              min="1"
              step="1"
              class="price-input"
              @input="
              (e) => {
                const v = Number(e?.target?.value);
                if (!Number.isNaN(v) && v <= 0) data.item.cost = 1;
              }
            "
            @blur="
              () => {
                const v = Number(data.item.cost);
                if (!Number.isNaN(v) && v <= 0) data.item.cost = 1;
                editingIndex = null;
              }
            "
              @keyup.enter="editingIndex = null"
              v-focus
            ></b-form-input>
          </div>
        </template>
        <template #cell(lastModified)="data">
            {{  formatLastModified(data.item.lastModified)  }}
          </template>
      </b-table>


    </b-card>

    <b-modal
      ref="confirmation-modal"
      centered
      hide-header
      hide-footer
      dialog-class="confirm-import-modal"
    >
      <div
        class="d-flex justify-content-center flex-column align-items-center text-center"
      >
        <div class="message-detail">
          <h5 class="msg-title upload-confirmation-msg">
            {{ $t(`${moduleSlug}.upload_confirmation-msg`) }}
          </h5>
          <div class="d-flex justify-content-center">
            <b-button
              variant="brand-secondary"
              class="mr-2 cancel-btn"
              @click="hideConfirmationModal"
            >
            {{ $t(`${moduleSlug}.upload_confirmation_cancel_btn-label`) }}
            </b-button>
            <b-button
              variant="brand-primary"
              class="confirm-btn"
              @click="submitForm"
            >
            {{ $t(`${moduleSlug}.upload_confirmation_proceed_btn-label`) }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      dialog-class="success-modal"
      centered
      hide-header
      hide-footer
      ref="success-modal"
    >
      <div
        class="d-flex justify-content-center flex-column align-items-center text-center"
      >
        <i class="font-50 icon-completed mb-2 text-success"></i>
        <div class="message-detail">
          <h5 class="msg-title font-weight-bold">
            {{ $t(`${moduleSlug}.success_message-title`) }}
          </h5>
          <i18n
            :path="`${moduleSlug}.success_message-desc`"
            tag="p"
            class="msg-desc m-0"
          >
            <b-link to="/pricing-history">{{
              $t(`${moduleSlug}.success_message-desc_term`)
            }}</b-link>
          </i18n>
        </div>
      </div>
      </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import { required, minLength } from "vuelidate/lib/validators";
import { forEach, isEmpty } from "lodash";
import moment from "moment";
import config from "../../../../config";
import PreviewForm from "@/components/PreviewForm.vue";
import { detectSkuNotMatch as detectSkuNotMatchHelper } from "../../../shared/helper";
const { dateFormat } = config;

export default {
  name: "CostManagement",
  metaInfo: {
    title: "Cost Management",
  },
  components: {
    PreviewForm,
  },
  props: {
    moduleSlug: {
      type: String,
      default: "cost-management",
    }
  },
  data() {
    return {
      showForm: true,
      showFormPreview: false,
      showValidationTable: false,
      csvValidated: false,
      invalidCSV: false,
      emptyCSV: false,
      emptySkuCsv: false,
      emptyPriceCsv: false,
      duplicateCSVData: false,
      formData: {
        id: 0,
        name: "",
        sites: [],
        csvFile: null,
        priceStartDate: new Date(),
        priceEndDate: "",
      },
      csvFileData: [],
      validationRows: [],
      dateFormat: dateFormat,
      masks: {
        input: dateFormat,
      },
      modelConfig: {
        type: "string",
        mask: "iso",
      },
      editingIndex: null,
    };
  },
  directives: {
    focus: {
      inserted(el) {
        el.focus();
      },
    },
  },
  computed: {
    ...mapGetters("auth", ["siteAccess"]),
    ...mapGetters(["selected_language"]),
    ...mapGetters("pricingManager", ["mode", "preFilledData", "pricingManagerId"]),
    validationFieldsDef() {
      return [
        {
          key: "product",
          label: this.$t(`${this.moduleSlug}.table_head_products`),
          tdClass: "filtered-products",
          thClass: ["text-nowrap", "pl-0"],
        },
        {
          key: "stores",
          label: this.$t(`${this.moduleSlug}.table_head_siteName`),
          thClass: ["text-nowrap", "stores-column"],
          tdClass: ["text-wrap", "stores-column"],
        },
        { key: "sku", label: this.$t(`${this.moduleSlug}.table_head_sku`), thClass: "text-nowrap" },
        { key: "vmn", label: this.$t(`${this.moduleSlug}.table_head_vmn`), thClass: "text-nowrap" },
        { key: "cost", label: this.$t(`${this.moduleSlug}.table_head_cost`), thClass: "text-nowrap" },
        { key: "price", label: this.$t(`${this.moduleSlug}.table_head_price`), thClass: "text-nowrap" },
        { key: "lastModified", label: this.$t(`${this.moduleSlug}.table_head_lastmodified`), thClass: "text-nowrap" },
        { key: "status", label: '', thClass: "text-nowrap" },
      ];
    },
    hasErrors() {
      return this.validationRows.some(row => row.status === 'error');
    },
    canEditValidationRows() {
      return this.mode !== 'edit' || this.csvValidated;
    },
    validationFields() {
      if (this.mode === 'edit' && !this.csvValidated) {
        return this.validationFieldsDef.filter((field) => field.key !== 'status');
      }
      return this.validationFieldsDef.filter((field) => field.key !== 'lastModified');
    },
    CSVFileName() {
      return this.formData.csvFile ? this.formData.csvFile.name : this.preFilledData ? this.preFilledData.Name + ".csv" : "";
    },
    formattedStartDate() {
      return this.formData.priceStartDate ? moment(this.formData.priceStartDate).format(this.dateFormat) : "";
    },
    formattedEndDate() {
      return (this.formData.priceEndDate && this.formData.priceEndDate !== "") ? moment(this.formData.priceEndDate).format(this.dateFormat) : "";
    },
    siteOptions() {
      let options = [];
      forEach(this.siteAccess, (site) => {
        let obj = {
          text: site.Name,
          value: { siteId: site.SiteId, siteName: site.Name },
        };
        options.push(obj);
      });
      return options;
    },
    yesterday() {
      const date = new Date();
      date.setDate(date.getDate() - 1);
      return date;
    },
    selectAll: function () {
      return this.formData.sites.length > 0 && this.formData.sites.length === this.siteOptions.length;
    },
  },
  mounted() {
    //if (this.preFilledData && typeof this.preFilledData === "object"){a
    if (this.preFilledData && typeof this.preFilledData === "object" && (this.preFilledData.PriceTypeId == "4" || this.preFilledData.priceTypeId == "4")) {
      const data = this.preFilledData;

      this.formData.id =
        this.mode === "edit" ? data.Id || data.id || 0 : 0;

      this.formData.name =
        this.mode === "edit" ? data.Name : `Copy of ${data.Name}`;

      // Fix: Handle SiteIds (plural array) or SiteId (singular)
      const dataSiteIds = data.SiteIds || (data.SiteId ? [data.SiteId] : []);
      this.formData.sites = this.siteOptions
        .filter((s) => dataSiteIds.includes(s.value.siteId))
        .map((s) => s.value);
       
      this.formData.priceStartDate = new Date(data.PriceStartDate);
      this.formData.priceEndDate =
        data.PriceEndDate === "2099-12-31T00:00:00Z" ||
        data.PriceEndDate === "2099-12-31T00:00:00"
          ? ""
          : new Date(data.PriceEndDate);

      const costPricingData = data.PricingData || data.costpricing || data.CostPricing || data.costPricing || data.pricingData || data.PricingData || data.flyerpricing || data.flyerPricing || data.promotionalPricing || data.promotionalpricing || data.everydayPricing || data.everydaypricing || (data.productcondition && data.productcondition[0] ? (data.productcondition[0].costpricing || data.productcondition[0].flyerpricing || data.productcondition[0].pricingData) : null) || [];
      if (costPricingData && costPricingData.length) {
        this.validationRows = costPricingData.map((item) => ({
          productName: item.productName || item.ProductName || item.products || item.item_name || `SKU ${item.productSKU || item.ProductSKU || item.sku || item.productId}`,
          stores: item.siteNames || item.SiteNames || (item.SiteName ? [item.SiteName] : (item.siteName ? [item.siteName] : (item.stores ? item.stores : (data.siteNames || data.SiteNames || [])))),
          siteId: item.siteId || item.SiteId || (item.siteIds ? item.siteIds[0] : (item.SiteIds ? item.SiteIds[0] : (data.siteIds ? data.siteIds[0] : (data.SiteIds ? data.SiteIds[0] : null)))),
          siteName: item.siteName || item.SiteName || (item.siteNames ? item.siteNames[0] : (item.SiteNames ? item.SiteNames[0] : (data.siteNames ? data.siteNames[0] : (data.SiteNames ? data.SiteNames[0] : null)))),
          productId: item.productId || item.productSKU || item.ProductSKU || item.sku || item.product_id,
          productSKU: item.productSKU || item.ProductSKU || item.sku || item.productId,
          vmn: item.vendorModelNumber || item.VendorModelNumber || item.vmn || item.vnm || item.modelNumber || "",
          cost: item.cost || item.Cost || item.replacementCost  || 0,
          price: item.regularPrice  || item.price || item.Price || "",
          lastModified: item.lastModified ||item.createdOn || "",
          status: 'valid',
          statusMessage: 'Valid',
        }));
        this.showValidationTable = true;
      }

      if (this.mode === "edit") {
        this.showForm = false;
        this.showFormPreview = true;
      }
    }
  },
  methods: {
    ...mapActions("pricingManager", ["verifyImportedCsv", "submitImportedCsv"]),
    ...mapMutations(["setItem"]),
    ...mapMutations("pricingManager", { pricingSetItem: "setItem" }),
    detectSkuNotMatch(item) {
      return detectSkuNotMatchHelper(item);
    },
    deleteSite(index) {
      this.formData.sites.splice(index, 1);
    },
    toggleSelectAll(checked) {
      let selected = [];
      if (checked) {
        this.siteOptions.forEach((site) => {
          selected.push(site.value);
        });
      }
      this.formData.sites = selected;
    },
    removeCSVFile() {
      this.formData.csvFile = null;
      this.invalidCSV = false;
      this.emptyCSV = false;
      this.emptySkuCsv = false;
      this.emptyPriceCsv = false;
      this.duplicateCSVData = false;
      this.csvFileData = [];
    },
    formatLastModified(date) {
      if (!date) return "-";
      return moment(date).format("MMM D, YYYY h:mm A");
    },
    async createInput(file) {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.onerror = (event) => {
          reject(new Error(`Failed to read file: ${file.name}`));
        };
        reader.readAsText(file);
      });
    },
    async parseCSV() {
      if (!this.formData.csvFile) {
        this.csvFileData = [];
        return;
      }
      
      try {
        let fileContent = await this.createInput(this.formData.csvFile);
        let delimiter = this.csvHelpers.guessDelimiters(fileContent, ["\t", ","]);
        if (delimiter.length === 0) {
          this.csvFileData = [];
          return;
        }
        let jsonContent = JSON.parse(
          this.csvHelpers.CSVToJSON(fileContent, delimiter)
        );

        // Reset flags
        this.emptySkuCsv = false;
        this.emptyPriceCsv = false;
        this.duplicateCSVData = false;

        const normalizeCell = (v) => {
          if (v === null || v === undefined) return "";
          let s = String(v).trim();
          // Strip wrapping quotes if present (common when users edit in Excel)
          if (s.length >= 2 && s[0] === `"` && s[s.length - 1] === `"`) {
            s = s.slice(1, -1).trim();
          }
          return s;
        };

        const getCaseInsensitive = (obj, key) => {
          if (!obj || typeof obj !== "object") return undefined;
          if (key in obj) return obj[key];
          const found = Object.keys(obj).find((k) => String(k).trim().toLowerCase() === key.toLowerCase());
          return found ? obj[found] : undefined;
        };

        const seenSkus = new Set();
        const uniqueRows = [];
        jsonContent.forEach((row) => {
          const sku = normalizeCell(getCaseInsensitive(row, "sku"));
          const cost = normalizeCell(getCaseInsensitive(row, "cost"));

          // Ignore fully blank rows (including trailing blank lines in CSV)
          if (!sku && !cost) {
            return;
          }

          if (!sku) {
            this.emptySkuCsv = true;
          }
          if (!cost) {
            this.emptyPriceCsv = true;
          }

          if (sku && !seenSkus.has(sku)) {
            seenSkus.add(sku);
            uniqueRows.push({ sku, cost });
          } else if (sku && seenSkus.has(sku)) {
            this.duplicateCSVData = true;
          }
        });

        this.csvFileData = uniqueRows;
      } catch (error) {
        console.error("Error parsing CSV:", error);
        this.csvFileData = [];
        this.invalidCSV = true;
      }
    },
    async handleValidate() {
      this.invalidCSV = false;
      this.emptyCSV = false;
      this.emptySkuCsv = false;
      this.emptyPriceCsv = false;
      this.duplicateCSVData = false;

      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) {
        return;
      }

      await this.parseCSV();

      if (this.csvFileData.length === 0) {
        this.emptyCSV = true;
        return;
      }

      // After parseCSV we normalize rows to { sku, cost }.
      const firstRow = this.csvFileData[0];
      if (!firstRow || !("sku" in firstRow) || !("cost" in firstRow)) {
        this.invalidCSV = true;
        return;
      }

      if (this.emptySkuCsv || this.emptyPriceCsv || this.duplicateCSVData) {
        return;
      }

      this.setItem({ resource: "isLoading", payload: true });

      const payload = {
        pricingManagerId: this.formData.id || this.pricingManagerId || 0,
        userId: this.loggedUserId,
        priceStartDate: new Date(this.formData.priceStartDate).toISOString(),
        priceEndDate: this.formData.priceEndDate
          ? new Date(this.formData.priceEndDate).toISOString()
          : "2099-12-31T00:00:00Z",
        siteIds: this.formData.sites.map(s => s.siteId),
        priceTypeId: "4",
        csv: this.csvFileData.map(row => ({
          // sku: row.sku,
          productId: row.sku,
          cost: Number(row.cost)
        }))
      };

      try {
        const response = await this.verifyImportedCsv({
          data: payload,
          exceptionParams: {
            userId: this.loggedUserId,
            controllerName: "CostManagement.vue",
            moduleName: "Cost Management",
          },
        });

        if (response.status === 1 && response.data) {
          if (response.pricingManagerId) {
            this.formData.id = response.pricingManagerId;
          }
          const resultRows = Array.isArray(response.data)
            ? response.data
            : [];
          this.validationRows = resultRows.map((item) => {
            const skuNotInSite =
              item.statusMessages &&
              item.statusMessages.some((msg) => msg.includes("sku-not-in-site"));
            const skuNotMatch = detectSkuNotMatchHelper(item);
            const firstStatusMessage = item.statusMessages && item.statusMessages[0]
              ? item.statusMessages[0]
              : "";
            const translateStatusMessage = (msg) => {
              if (!msg) return "";
              const key = `general.${msg}`;
              return this.$te(key) ? this.$t(key) : msg;
            };

            let status = "warning";
            let statusMessage = translateStatusMessage(firstStatusMessage);

            if (skuNotMatch || item.status === 0) {
              status = "error";
              statusMessage = translateStatusMessage(firstStatusMessage || "sku-not-match");
            } else if (skuNotInSite) {
              status = "warning";
              statusMessage = translateStatusMessage("sku-not-in-site");
            } else if (item.status === 1) {
              status = "valid";
              statusMessage = translateStatusMessage(firstStatusMessage);
            }

            return {
              productName: skuNotMatch ? null : item.productName,
              stores: item.siteNames || [],
              productId: item.productId,
              productSKU: item.sku,
              vmn: skuNotMatch ? null : (item.vendorModelNumber || ""),
              cost: skuNotInSite || skuNotMatch ? null : item.cost,
              price: skuNotInSite || skuNotMatch ? null : item.compareAtPrice,
              skuNotInSite,
              skuNotMatch,
              status,
              statusMessage,
            };
          });
          
          this.showForm = false;
          this.showFormPreview = true;
          this.showValidationTable = true;
          this.csvValidated = true;
        }
      } catch (error) {
        console.error("Error validating CSV:", error);
      } finally {
        this.setItem({ resource: "isLoading", payload: false });
      }
    },
    handleEdit() {
      this.showForm = true;
      this.showValidationTable = false;
      this.showFormPreview = false;
      this.csvValidated = false;
      this.editingIndex = null;
    },
    showStartDatePicker() {
      this.$nextTick(() => {
        this.$refs.priceEndDate && this.$refs.priceEndDate.hidePopover();
        this.$refs.priceStartDate && this.$refs.priceStartDate.togglePopover();
      });
    },
    showEndDatePicker() {
      this.$nextTick(() => {
        this.$refs.priceStartDate && this.$refs.priceStartDate.hidePopover();
        this.$refs.priceEndDate && this.$refs.priceEndDate.togglePopover();
      });
    },
    downloadCSVTemplate() {
      this.generateCSV(true);
    },
    downloadProductPreview() {
      this.generateCSV(false);
    },
    generateCSV(isTemplate) {
      try {
        this.setItem({ resource: "isLoading", payload: true });
        const headers = { sku: "SKU", cost: "Cost" };
        let csvData = [];

        if (isTemplate && (this.mode === "" || this.mode === null)) {
          // csvData.push({ sku: '""', cost: '""' });
        } else {
          csvData = this.validationRows.map((row) => ({
            sku: row.productSKU ? `"${row.productSKU}"` : '""',
            cost: row.cost !== undefined && row.cost !== null ? `"${row.cost}"` : '""',
          }));
        }

        const dateStr = moment(new Date()).format("YYYY/MM/DD");
        const fileTitle =
          isTemplate && (this.mode === "" || this.mode === null)
            ? `Cost_Management_Template_${dateStr}`
            : `${this.formData.name}_Cost_Change_${dateStr}`;

        this.setItem({ resource: "isLoading", payload: false });
        this.exportCSVFile(headers, csvData, fileTitle);
      } catch {
        this.setItem({ resource: "isLoading", payload: false });
      }
    },
    confirmSubmit() {
      this.$refs["confirmation-modal"].show();
    },
    hideConfirmationModal() {
      this.$refs["confirmation-modal"].hide();
    },
    async submitForm() {
      this.setItem({ resource: "isLoading", payload: true });

      // One entry per SKU (same approach as promo/flyer/everyday import submit)
      const uniquePricingData = [];
      const productKeys = new Set();
      this.validationRows
        .filter((row) => row.status === "valid")
        .forEach((row) => {
          const productId = row.productId;
          if (!productId || productKeys.has(String(productId))) return;
          productKeys.add(String(productId));
          uniquePricingData.push({
            productId: productId,
            cost: Number(row.cost),
          });
        });

      const payload = {
        id: this.formData.id || this.pricingManagerId || 0,
        siteIds: this.formData.sites.map(s => s.siteId),
        userId: this.loggedUserId,
        name: this.formData.name,
        priceTypeId: "4",
        priceStartDate: new Date(this.formData.priceStartDate).toISOString(),
        priceEndDate: this.formData.priceEndDate ? new Date(this.formData.priceEndDate).toISOString() : "2099-12-31T00:00:00Z",
        pricingData: uniquePricingData,
        exceptionParams: {
          userId: this.loggedUserId,
          controllerName: "CostManagement.vue",
          moduleName: "Cost Management",
        },
      }

      try {
        const response = await this.submitImportedCsv(payload);
        
        if (response.status === 1) {
          this.hideConfirmationModal();
          this.$refs["success-modal"].show();
          this.resetForm();
        }
      } catch (error) {
        console.error("Error submitting cost changes:", error);
      } finally {
        this.setItem({ resource: "isLoading", payload: false });
      }
    },
    resetForm() {
      this.showForm = true;
      this.showValidationTable = false;
      this.showFormPreview = false;
      this.csvValidated = false;
      this.invalidCSV = false;
      this.emptyCSV = false;
      this.emptySkuCsv = false;
      this.emptyPriceCsv = false;
      this.duplicateCSVData = false;
      this.csvFileData = [];
      this.validationRows = [];
      this.formData = {
        id: 0,
        name: "",
        sites: [],
        csvFile: null,
        priceStartDate: new Date(),
        priceEndDate: "",
      };
      this.pricingSetItem({ resource: "pricingManagerId", payload: 0 });
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },
    rowClass(item) {
      if (!item) return;

      if (item.status === "error") {
        return "tr-error";
      }

      if (item.status === "warning") {
        return "tr-warning";
      }

      return "";
    }
  },
  beforeDestroy() {
    this.pricingSetItem({ resource: "pricingManagerId", payload: 0 });
    this.pricingSetItem({ resource: "mode", payload: "" });
    this.pricingSetItem({ resource: "preFilledData", payload: null });
  },
  watch: {
    selected_language() {
      this.$v.$reset();
    },
    "formData.sites": function (newValue) {
      this.setItem({ resource: "selectedSite", payload: newValue });
    },
  },
  validations: {
    formData: {
      name: { required },
      sites: { required, minLength: minLength(1) },
      csvFile: {
        required,
        fileValidation(value) {
          if (value != null) {
            let allowedExtensions = ["csv"];
            let fileExtension = value.name
              .substring(value.name.lastIndexOf(".") + 1)
              .toLowerCase();
            if (allowedExtensions.indexOf(fileExtension) <= -1) {
              return false;
            }
          }
          return true;
        },
      },
      priceStartDate: {
        required,
        minValue(value) {
          let sdate = moment(value);
          sdate.set("hour", 0);
          sdate.set("minute", 0);
          sdate.set("second", 0);
          sdate.set("millisecond", 0);

          let edate = "";

          if (!isEmpty(this.formData.priceEndDate)) {
            edate = moment(this.formData.priceEndDate);
            edate.set("hour", 0);
            edate.set("minute", 0);
            edate.set("second", 0);
            edate.set("millisecond", 0);

            if (
              moment(edate)._i == "2099-12-31T00:00:00" ||
              moment(edate)._i == "2099-12-31T00:00:00Z" ||
              moment(edate)._i == "2099-12-31T00:00:00.000Z"
            ) {
              edate = "";
            }
          }

          let status = this.checkStartDateValidation(sdate, edate, this.mode);

          return status;
        },
      },
      priceEndDate: {
        minValue(value) {
          let sdate = moment(this.formData.priceStartDate);
          sdate.set("hour", 0);
          sdate.set("minute", 0);
          sdate.set("second", 0);
          sdate.set("millisecond", 0);

          let edate = moment(value);
          edate.set("hour", 0);
          edate.set("minute", 0);
          edate.set("second", 0);
          edate.set("millisecond", 0);

          if (
            moment(edate)._i == "2099-12-31T00:00:00" ||
            moment(edate)._i == "2099-12-31T00:00:00Z" ||
            moment(edate)._i == "2099-12-31T00:00:00.000Z"
          ) {
            edate = "";
          }

          let status = this.checkEndDateValidation(sdate, edate);

          return status;
        },
      },
    },
  },
};
</script>

<style scoped>
.price-edit-container {
  width: 120px;
}

.price-text {
  padding: 0.375rem 0.75rem;
  height: calc(1.5em + 0.75rem + 2px);
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  width: 100%;
}

.price-input {
  width: 100%;
}

.price-text.clickable:hover {
  cursor: pointer;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-color: #dee2e6;
}

/* NEW TABLE STYLES */

::v-deep .tr-error {
  background-color: #fff5f5 !important;
}

::v-deep .tr-warning {
  background-color: #fff9e6 !important;
}

::v-deep .table td {
  vertical-align: middle;
}

.badge-orange-badge {
  background-color: #FBB040;
  color: #212121;
  padding: 3.008px 10px;
  font-weight: 500;
  border-radius: 20px;
  font-size: 11px;
}

.status-icon-container {
  display: flex;
  justify-content: center;
}

.upload-confirmation-msg {
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  margin-bottom: 15px;
}
</style>




